import { DEFAULT_LANGUAGE } from "@bounce/i18n"

import { MARKETING_PROJECT_DOMAIN, USEBOUNCE_PROJECT_DOMAIN } from "./constants"
import I18N_DOMAINS from "../../../next-i18next-domains.config.json"
import { config } from "../../config"

export type GetDomainOptions = {
  language: string
  defaultDomain?: string
  enableI18nDomains?: boolean
}

export type I18nDomainConfig = {
  domain: string
  defaultLocale: string
  locales?: string[]
}

/**
 * Returns the domain for the given language based on next-i18next configuration.
 *
 * @param language - The language to get the domain for.
 * @returns The domain for the given language.
 * @example
 * getDomain({ language: "zh", enableI18nDomains: true }) // { domain: "zh.bounce.com", isDefaultLanguage: true }
 * getDomain({ language: "en", enableI18nDomains: true }) // { domain: "bounce.com", isDefaultLanguage: true }
 * getDomain({ language: "de", enableI18nDomains: true }) // { domain: "bounce.com", isDefaultLanguage: true }
 * getDomain({ language: "zh", enableI18nDomains: false }) // { domain: "localhost", isDefaultLanguage: false }
 */
export const getDomain = ({
  language,
  defaultDomain = config.domain,
  enableI18nDomains = config.enableI18nDomains,
}: GetDomainOptions) => {
  let domain = defaultDomain

  const isEnglish = language === DEFAULT_LANGUAGE
  let isDefaultLanguage = !enableI18nDomains && isEnglish

  if (!enableI18nDomains) {
    return {
      domain,
      isDefaultLanguage,
    }
  }

  // Find the appropriate domain for the language from next-i18next config
  // eg: "zh" -> "zh.bounce.com"
  const i18nDomainConfig = getI18nDomainConfigFromLanguage(language)
  if (i18nDomainConfig) {
    return {
      domain: i18nDomainConfig.domain,
      isDefaultLanguage: i18nDomainConfig.defaultLocale === language,
    }
  }

  return {
    domain,
    isDefaultLanguage,
  }
}

/**
 * Get i18n config for a domain
 * If the domain is marketing.bounce.com it use bounce.com by default
 */
export const getI18nDomainConfig = (domain: string): I18nDomainConfig | undefined => {
  // If marketing.bounce.com replace the domain by bounce.com
  const i18nDomain = domain === MARKETING_PROJECT_DOMAIN ? USEBOUNCE_PROJECT_DOMAIN : domain
  const domainConfig = I18N_DOMAINS.find(domainConfig => domainConfig.domain === i18nDomain)

  return domainConfig
}

/**
 * Get i18n config for a language
 */
export const getI18nDomainConfigFromLanguage = (language: string): I18nDomainConfig | undefined => {
  return I18N_DOMAINS.find(
    domainConfig => domainConfig.locales?.includes(language) || domainConfig.defaultLocale === language,
  )
}
